import React, { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  IconButton,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

/**
 * 숫자 콤마 포맷
 */
const formatNumber = (num, decimalPoint = 0) => {
  if (num === null || num === undefined || num === '') return '';
  const number = Number(num);
  if (isNaN(number)) return '';
  
  if (decimalPoint > 0) {
    return number.toLocaleString('ko-KR', { 
      minimumFractionDigits: decimalPoint,
      maximumFractionDigits: decimalPoint 
    });
  }
  return number.toLocaleString('ko-KR');
};

/**
 * 배분 방식 옵션
 */
const DISTRIBUTION_OPTIONS = [
  { value: 'equal', label: '균등 배분', desc: '12개월 동일하게' },
  { value: 'quarterly', label: '분기별 배분', desc: '분기별 비율 지정' },
  { value: 'weighted', label: '가중치 배분', desc: '상/하반기 비율' },
  { value: 'custom', label: '월별 비율', desc: '월별 비율 직접 지정' },
];

/**
 * 목표 설정 테이블 컴포넌트
 * - 행: 1~12월
 * - 열: KPI 항목들
 * - 연간 목표 자동 배분 기능
 */
const TargetTable = ({ kpiList, monthlyData, onValueChange, onBulkUpdate, disabled = false }) => {
  
  // 배분 다이얼로그 상태
  const [distributeDialogOpen, setDistributeDialogOpen] = useState(false);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [yearlyTotal, setYearlyTotal] = useState('');
  const [distributionType, setDistributionType] = useState('equal');
  const [quarterRatios, setQuarterRatios] = useState({ Q1: 25, Q2: 25, Q3: 25, Q4: 25 });
  const [halfRatios, setHalfRatios] = useState({ first: 50, second: 50 });
  const [previewData, setPreviewData] = useState([]);

  // KPI별 연간 합계 계산
  const yearlyTotals = useMemo(() => {
    const totals = {};
    kpiList.forEach((kpi) => {
      totals[kpi.kpiCd] = monthlyData.reduce(
        (sum, month) => sum + (Number(month.kpiValues?.[kpi.kpiCd]) || 0),
        0
      );
    });
    return totals;
  }, [kpiList, monthlyData]);

  // KPI별 분기 합계 계산
  const quarterTotals = useMemo(() => {
    const totals = { Q1: {}, Q2: {}, Q3: {}, Q4: {} };
    kpiList.forEach((kpi) => {
      totals.Q1[kpi.kpiCd] = monthlyData.slice(0, 3).reduce((sum, m) => sum + (Number(m.kpiValues?.[kpi.kpiCd]) || 0), 0);
      totals.Q2[kpi.kpiCd] = monthlyData.slice(3, 6).reduce((sum, m) => sum + (Number(m.kpiValues?.[kpi.kpiCd]) || 0), 0);
      totals.Q3[kpi.kpiCd] = monthlyData.slice(6, 9).reduce((sum, m) => sum + (Number(m.kpiValues?.[kpi.kpiCd]) || 0), 0);
      totals.Q4[kpi.kpiCd] = monthlyData.slice(9, 12).reduce((sum, m) => sum + (Number(m.kpiValues?.[kpi.kpiCd]) || 0), 0);
    });
    return totals;
  }, [kpiList, monthlyData]);

  // 배분 다이얼로그 열기
  const openDistributeDialog = (kpi) => {
    setSelectedKpi(kpi);
    setYearlyTotal(yearlyTotals[kpi.kpiCd] || '');
    setDistributionType('equal');
    setQuarterRatios({ Q1: 25, Q2: 25, Q3: 25, Q4: 25 });
    setHalfRatios({ first: 50, second: 50 });
    setPreviewData([]);
    setDistributeDialogOpen(true);
  };

  // 배분 미리보기 계산
  const calculateDistribution = () => {
    const total = Number(yearlyTotal) || 0;
    if (total <= 0) return;

    let distributed = [];
    const decimalPoint = selectedKpi?.decimalPoint || 0;

    switch (distributionType) {
      case 'equal': {
        // 균등 배분
        const monthlyAmount = Math.floor(total / 12);
        const remainder = total - (monthlyAmount * 12);
        distributed = Array(12).fill(monthlyAmount);
        // 나머지는 앞 월부터 1씩 배분
        for (let i = 0; i < remainder; i++) {
          distributed[i] += 1;
        }
        break;
      }
      case 'quarterly': {
        // 분기별 배분
        const q1Total = Math.round(total * quarterRatios.Q1 / 100);
        const q2Total = Math.round(total * quarterRatios.Q2 / 100);
        const q3Total = Math.round(total * quarterRatios.Q3 / 100);
        const q4Total = total - q1Total - q2Total - q3Total; // 나머지는 4분기에

        const distributeQuarter = (quarterTotal) => {
          const monthly = Math.floor(quarterTotal / 3);
          const rem = quarterTotal - (monthly * 3);
          const arr = [monthly, monthly, monthly];
          for (let i = 0; i < rem; i++) arr[i] += 1;
          return arr;
        };

        distributed = [
          ...distributeQuarter(q1Total),
          ...distributeQuarter(q2Total),
          ...distributeQuarter(q3Total),
          ...distributeQuarter(q4Total),
        ];
        break;
      }
      case 'weighted': {
        // 상/하반기 가중치 배분
        const firstHalfTotal = Math.round(total * halfRatios.first / 100);
        const secondHalfTotal = total - firstHalfTotal;

        const distributeHalf = (halfTotal) => {
          const monthly = Math.floor(halfTotal / 6);
          const rem = halfTotal - (monthly * 6);
          const arr = Array(6).fill(monthly);
          for (let i = 0; i < rem; i++) arr[i] += 1;
          return arr;
        };

        distributed = [
          ...distributeHalf(firstHalfTotal),
          ...distributeHalf(secondHalfTotal),
        ];
        break;
      }
      default:
        distributed = Array(12).fill(Math.floor(total / 12));
    }

    setPreviewData(distributed);
  };

  // 배분 적용
  const applyDistribution = () => {
    if (!selectedKpi || previewData.length !== 12) return;

    // 월별 데이터 업데이트
    previewData.forEach((value, index) => {
      onValueChange(index + 1, selectedKpi.kpiCd, value);
    });

    setDistributeDialogOpen(false);
  };

  // 분기 소계 행 렌더링
  const renderQuarterRow = (quarter, label, bgColor) => (
    <TableRow key={quarter} sx={{ backgroundColor: bgColor }}>
      <TableCell 
        align="center"
        sx={{ 
          fontWeight: 'bold', 
          position: 'sticky', 
          left: 0, 
          backgroundColor: bgColor,
          zIndex: 1,
        }}
      >
        {label}
      </TableCell>
      {kpiList.map((kpi) => (
        <TableCell key={kpi.kpiCd} align="right" sx={{ fontWeight: 'bold' }}>
          {formatNumber(quarterTotals[quarter][kpi.kpiCd], kpi.decimalPoint || 0)}
        </TableCell>
      ))}
    </TableRow>
  );

  if (!kpiList.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          해당 목표 유형의 KPI가 정의되지 않았습니다.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            {/* KPI 헤더 + 자동배분 버튼 행 */}
            <TableRow>
              <TableCell 
                align="center" 
                sx={{ 
                  fontWeight: 'bold', 
                  width: 80, 
                  position: 'sticky', 
                  left: 0, 
                  backgroundColor: '#f5f5f5',
                  zIndex: 3,
                }}
              >
                월
              </TableCell>
              {kpiList.map((kpi) => (
                <TableCell 
                  key={kpi.kpiCd} 
                  align="center"
                  sx={{ 
                    fontWeight: 'bold', 
                    minWidth: 130,
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    {kpi.kpiNm}
                    {!disabled && (
                      <Tooltip title="연간 목표 자동 배분">
                        <IconButton 
                          size="small" 
                          onClick={() => openDistributeDialog(kpi)}
                          sx={{ ml: 0.5 }}
                        >
                          <AutoFixHighIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="caption" display="block" color="text.secondary">
                    ({kpi.kpiUnit})
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {monthlyData.map((monthItem, idx) => (
              <React.Fragment key={monthItem.month}>
                <TableRow hover>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontWeight: 'bold', 
                      position: 'sticky', 
                      left: 0, 
                      backgroundColor: '#fff',
                      zIndex: 1,
                    }}
                  >
                    {monthItem.month}월
                  </TableCell>
                  {kpiList.map((kpi) => (
                    <TableCell key={kpi.kpiCd} align="right" sx={{ p: 0.5 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={monthItem.kpiValues?.[kpi.kpiCd] || ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, '');
                          onValueChange(monthItem.month, kpi.kpiCd, val);
                        }}
                        disabled={disabled}
                        inputProps={{
                          style: { textAlign: 'right', padding: '6px 8px' },
                          inputMode: kpi.dataTyp === 'DECIMAL' ? 'decimal' : 'numeric',
                        }}
                        sx={{ 
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                          },
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
                
                {/* 분기 소계 행 */}
                {idx === 2 && renderQuarterRow('Q1', '1분기', '#e3f2fd')}
                {idx === 5 && renderQuarterRow('Q2', '2분기', '#e8f5e9')}
                {idx === 8 && renderQuarterRow('Q3', '3분기', '#fff3e0')}
                {idx === 11 && renderQuarterRow('Q4', '4분기', '#fce4ec')}
              </React.Fragment>
            ))}

            {/* 연간 합계 행 */}
            <TableRow sx={{ backgroundColor: '#424242' }}>
              <TableCell 
                align="center"
                sx={{ 
                  fontWeight: 'bold', 
                  position: 'sticky', 
                  left: 0, 
                  backgroundColor: '#424242',
                  color: '#fff',
                  zIndex: 1,
                }}
              >
                연간 합계
              </TableCell>
              {kpiList.map((kpi) => (
                <TableCell 
                  key={kpi.kpiCd} 
                  align="right" 
                  sx={{ fontWeight: 'bold', color: '#fff' }}
                >
                  {formatNumber(yearlyTotals[kpi.kpiCd], kpi.decimalPoint || 0)}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* 자동 배분 다이얼로그 */}
      <Dialog 
        open={distributeDialogOpen} 
        onClose={() => setDistributeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalculateIcon color="primary" />
          연간 목표 자동 배분
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {/* 선택된 KPI 정보 */}
            <Typography variant="subtitle2" color="primary" gutterBottom>
              {selectedKpi?.kpiNm} ({selectedKpi?.kpiUnit})
            </Typography>

            {/* 연간 총 목표 입력 */}
            <TextField
              fullWidth
              label="연간 총 목표"
              size="small"
              value={yearlyTotal}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '');
                setYearlyTotal(val);
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">{selectedKpi?.kpiUnit}</InputAdornment>,
              }}
              sx={{ mt: 2, mb: 2 }}
            />

            {/* 배분 방식 선택 */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>배분 방식</InputLabel>
              <Select
                value={distributionType}
                label="배분 방식"
                onChange={(e) => setDistributionType(e.target.value)}
              >
                {DISTRIBUTION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label} - {opt.desc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 분기별 비율 입력 */}
            {distributionType === 'quarterly' && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {['Q1', 'Q2', 'Q3', 'Q4'].map((q, idx) => (
                  <TextField
                    key={q}
                    size="small"
                    label={`${idx + 1}분기`}
                    value={quarterRatios[q]}
                    onChange={(e) => setQuarterRatios({ 
                      ...quarterRatios, 
                      [q]: Number(e.target.value) || 0 
                    })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    sx={{ width: '25%' }}
                  />
                ))}
              </Box>
            )}

            {/* 상/하반기 비율 입력 */}
            {distributionType === 'weighted' && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  size="small"
                  label="상반기 (1~6월)"
                  value={halfRatios.first}
                  onChange={(e) => setHalfRatios({ 
                    ...halfRatios, 
                    first: Number(e.target.value) || 0,
                    second: 100 - (Number(e.target.value) || 0)
                  })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={{ width: '50%' }}
                />
                <TextField
                  size="small"
                  label="하반기 (7~12월)"
                  value={halfRatios.second}
                  onChange={(e) => setHalfRatios({ 
                    ...halfRatios, 
                    second: Number(e.target.value) || 0,
                    first: 100 - (Number(e.target.value) || 0)
                  })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={{ width: '50%' }}
                />
              </Box>
            )}

            {/* 미리보기 버튼 */}
            <Button 
              variant="outlined" 
              onClick={calculateDistribution}
              startIcon={<CalculateIcon />}
              fullWidth
              sx={{ mb: 2 }}
            >
              배분 미리보기
            </Button>

            {/* 미리보기 결과 */}
            {previewData.length === 12 && (
              <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>배분 결과 미리보기</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {previewData.map((val, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        width: 'calc(25% - 8px)', 
                        textAlign: 'center',
                        p: 1,
                        backgroundColor: '#fff',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {idx + 1}월
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatNumber(val)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'right' }}>
                  합계: <strong>{formatNumber(previewData.reduce((a, b) => a + b, 0))}</strong>
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDistributeDialogOpen(false)}>취소</Button>
          <Button 
            variant="contained" 
            onClick={applyDistribution}
            disabled={previewData.length !== 12}
          >
            적용
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TargetTable;
